import { Capacitor } from '@capacitor/core';
import { listPreferenceKeys, preferencesStorage } from './preferences-storage';

// A namespace of our own, because `Preferences.keys()` under the `NativeStorage` group
// enumerates the whole backing store; without it the restore could not tell a mirrored
// value from one of Apple's own `UserDefaults` entries.
const MIRROR_PREFIX = 'ls.';

// `UserDefaults` and `SharedPreferences` are read in full at process start, so a large blob
// there is a launch cost paid on every launch. Above this a value is dropped from the mirror
// rather than pushed into it — the app degrades to today's behaviour for that one key.
const MAX_MIRRORED_BYTES = 64 * 1024;

// Nothing renders until the restore settles, so a bridge call that never answers is a
// permanently blank app. Overshooting costs a slow device a slower boot; not bounding it at
// all costs everyone the app.
const RESTORE_TIMEOUT_MS = 5_000;

// The budget above is what the native store reads back off disk, so it has to be measured in
// UTF-8 bytes: `String.length` counts UTF-16 code units, which understates non-Latin text by
// up to three times.
const encoder = new TextEncoder();

let started = false;
let failureReported = false;

// Only `clear()` needs this: the durable store holds keys the WebView has already evicted,
// and wiping those is what `clear()` means — but wiping anything we never wrote is not.
const mirroredKeys = new Set<string>();

const writeChains = new Map<string, Promise<void>>();

/**
 * Makes the WebView's `localStorage` survive an eviction on a native build, by restoring it
 * from `Preferences` at boot and echoing every later write back.
 *
 * **Native only, and that gate is load-bearing three times over.** On web the plugin's own
 * implementation of `Preferences.set` *is* `localStorage.setItem` under the very same key
 * (the `NativeStorage` group makes its prefix empty), so a mirror installed in a browser
 * would be re-entered by its own forwarded write, unboundedly — and would have nothing to
 * gain, since there the two stores are one store and it is already the durable one. Keeping
 * the patch off the web is also what makes the shared libraries' behaviour in the six
 * browser apps provably identical to before.
 *
 * Must complete before any module that reads `localStorage` while being evaluated —
 * see the entry point for how that ordering is arranged.
 *
 * Never rejects: its caller is the entry point, and there is nothing left for it to do with
 * a failure this has not already done.
 */
export async function restoreAndMirrorLocalStorage(): Promise<void> {
  // The whole function and not just the patch, so a second call cannot re-run the restore
  // against a `localStorage` the app has since written to.
  if (started || !Capacitor.isNativePlatform() || typeof window === 'undefined') {
    return;
  }
  started = true;

  // Empty rather than absent when the restore fails: nothing is confirmed mirrored, so the
  // sweep below has to consider every key. It only ever writes what `localStorage` holds, so
  // the keys a failed read left unseen in the durable store are not touched.
  let mirroredInStore = new Set<string>();

  try {
    // Restoring before patching, so the restore's own writes are not echoed straight back.
    mirroredInStore = await withTimeout(restoreFromPreferences(), RESTORE_TIMEOUT_MS);
  } catch (error) {
    reportMirrorFailure(error);
  } finally {
    // In a `finally` because a boot whose restore failed is exactly when the writes still
    // to come are worth keeping; skipping the patch would stretch one bad read into a whole
    // unprotected session.
    patchStorageWrites();
  }

  backfillFromLocalStorage(mirroredInStore);
}

/** Resolves once every mirror write started so far has reached the durable store. */
export function whenLocalStorageMirrorSettled(): Promise<void> {
  return Promise.all([...writeChains.values()]).then(() => undefined);
}

async function restoreFromPreferences(): Promise<Set<string>> {
  const prefixedKeys = await listPreferenceKeys(MIRROR_PREFIX);

  await Promise.all(prefixedKeys.map((prefixedKey) => restoreKey(prefixedKey)));

  return new Set(prefixedKeys);
}

async function restoreKey(prefixedKey: string): Promise<void> {
  const key = prefixedKey.slice(MIRROR_PREFIX.length);
  mirroredKeys.add(key);

  try {
    // Gaps are filled, never overwritten. The two stores are written together, so a
    // divergence means a write reached `localStorage` and not `Preferences`, which makes
    // `localStorage` the newer of the two. The one case this gets wrong is a key deleted
    // from `localStorage` whose removal never landed here — it comes back, once.
    if (globalThis.localStorage.getItem(key) !== null) {
      return;
    }

    const value = await preferencesStorage.getItem(prefixedKey);

    // Read again rather than trusting the check above: the timeout can hand the boot on
    // while this is still in flight, and by then the app may have written the key itself.
    if (value !== null && globalThis.localStorage.getItem(key) === null) {
      globalThis.localStorage.setItem(key, value);
    }
  } catch (error) {
    // Contained to its own key — one unreadable entry, or a `QuotaExceededError` on one
    // value, must not orphan every key queued behind it.
    reportMirrorFailure(error);
  }
}

// Everything `localStorage` already holds is invisible to `Preferences` until it is next
// written, and the keys this exists for — a dismissed banner, the chosen language, the last
// auth method — are written once and never again, so "next written" is never. Sweeping once
// is what stops the first eviction after release from taking them with it. Self-limiting:
// the sweep makes every key present, so later boots write nothing.
function backfillFromLocalStorage(alreadyMirrored: Set<string>): void {
  for (const key of Object.keys(globalThis.localStorage)) {
    if (alreadyMirrored.has(MIRROR_PREFIX + key)) {
      continue;
    }

    const value = globalThis.localStorage.getItem(key);

    if (value !== null) {
      mirrorWrite(key, value);
    }
  }
}

function patchStorageWrites(): void {
  const original = {
    setItem: Storage.prototype.setItem,
    removeItem: Storage.prototype.removeItem,
    clear: Storage.prototype.clear,
  };

  // `sessionStorage` shares this prototype, and making session-scoped data durable would
  // inseparably invert its contract — hence the receiver check on all three.
  const isLocalStorage = (receiver: Storage): boolean => receiver === globalThis.localStorage;

  Storage.prototype.setItem = function setItem(key: string, value: string): void {
    original.setItem.call(this, key, value);

    if (isLocalStorage(this)) {
      mirrorWrite(key, String(value));
    }
  };

  Storage.prototype.removeItem = function removeItem(key: string): void {
    original.removeItem.call(this, key);

    if (isLocalStorage(this)) {
      mirrorRemove(key);
    }
  };

  Storage.prototype.clear = function clear(): void {
    original.clear.call(this);

    if (isLocalStorage(this)) {
      for (const key of [...mirroredKeys]) {
        mirrorRemove(key);
      }
    }
  };
}

function mirrorWrite(key: string, value: string): void {
  if (encoder.encode(value).length > MAX_MIRRORED_BYTES) {
    mirrorRemove(key);
    return;
  }

  mirroredKeys.add(key);
  enqueue(key, () => preferencesStorage.setItem(MIRROR_PREFIX + key, value));
}

function mirrorRemove(key: string): void {
  mirroredKeys.delete(key);
  enqueue(key, () => preferencesStorage.removeItem(MIRROR_PREFIX + key));
}

// Per key, so a burst on one key cannot land out of order.
function enqueue(key: string, write: () => Promise<void>): void {
  const previous = writeChains.get(key) ?? Promise.resolve();

  writeChains.set(key, previous.then(write, write).catch(reportMirrorFailure));
}

function withTimeout<T>(work: Promise<T>, timeoutMs: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const expiry = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`Preferences did not answer within ${timeoutMs}ms`)), timeoutMs);
  });

  return Promise.race([work, expiry]).finally(() => clearTimeout(timer));
}

// A rejection escaping the synchronous `setItem` override would break the caller, and a
// bridge failure is nothing the user can act on — which is why each individual write
// swallows. Saying nothing at all is another matter in a feature whose premise is that
// losing settings quietly is the problem, so the first failure is reported once; the ones
// after it are the same failure repeating.
function reportMirrorFailure(error: unknown): void {
  if (failureReported) {
    return;
  }
  failureReported = true;

  console.error('localStorage mirror unavailable — settings may not survive an eviction', error);
}
